import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query';
import { useAuth } from './authStore.tsx';

/**
 * 인증이 필요한 조회는 **반드시 이 훅을 쓴다**(결정42, 결정46으로 게이트 기준 변경).
 *
 * 실사용 순서는 항상 "마운트 → GET /v1/auth/me 로 로그인 여부 확인 → (로그인 상태면)
 * 조회" 다. 화면이 이 순서를 건너뛰고 그냥 `useQuery` 를 쓰면 두 가지가 겹친다:
 *
 *   ① 로그인 여부가 확인되기 전에 요청이 먼저 나가 401 로 **에러 상태가 굳는다.**
 *   ② 로그인/로그아웃을 해도 쿼리 키가 그대로라 React Query 에겐 같은 쿼리다 —
 *      이미 실패했고 retry 도 소진돼 **재조회가 한 건도 나가지 않는다.**
 *
 * 이 훅이 두 가지를 한 번에 막는다:
 *   - `enabled` 에 로그인 확인 여부(`status === 'authenticated'`)를 곱한다 →
 *     미인증이면 요청 자체를 보내지 않는다. 로그인 전 무의미한 401 을 만들지 않는다.
 *   - 쿼리 키 **뒤에** sessionVersion 을 덧붙인다 → 로그인/로그아웃이 일어나면
 *     다른 쿼리가 되어 자동으로 새로 조회된다. 뒤에 붙이는 이유는 기존
 *     `invalidateQueries` 들이 접두사 부분일치에 의존하기 때문이다(앞에 붙이면
 *     전부 깨진다).
 */
export function useApiQuery<TData, TError = Error>(
  options: UseQueryOptions<TData, TError> & { queryKey: readonly unknown[] },
): UseQueryResult<TData, TError> {
  const { status, sessionVersion } = useAuth();
  const { queryKey, enabled, ...rest } = options;

  return useQuery({
    ...rest,
    queryKey: [...queryKey, { s: sessionVersion }],
    enabled: status === 'authenticated' && (enabled ?? true) !== false,
  } as UseQueryOptions<TData, TError>) as UseQueryResult<TData, TError>;
}
